import { inject } from '@angular/core';
import { CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { AuthService, PermissionCombinator } from './auth';

/**
 * Bounce to the login page but remember where the user was headed. Opening a
 * deep link in a fresh tab (middle click) starts without a token, so the guard
 * runs before login; without the target the OAuth round-trip drops the user on
 * the base url. Login passes it on as the OIDC `state`, which survives the
 * redirect to the identity provider and back.
 */
const toLogin = (router: Router, state: RouterStateSnapshot) =>
  router.createUrlTree([''], { queryParams: { redirect: state.url } });

export const AuthGuard: CanActivateFn = (_route, state) => {
  const auth = inject(AuthService);
  const router = inject(Router);

  if (auth.isLoggedIn()) {
    return true;
  }

  return toLogin(router, state);
};

/**
 * Studio feature flag, read off the ID token's `features` claim (`AuthService.hasFeature`). Composed
 * with `Auth(...)` in `canActivate`: the permission says what the user may do, the flag says what the
 * studio has. Without the flag the user lands on /tickets, exactly like a missing permission -- there
 * is no 403 page, and a bare `false` would leave them on a blank one.
 */
export const Feature: ((feature: string) => CanActivateFn) = (feature: string) => {
  return (_route, state) => {
    const auth = inject(AuthService);
    const router = inject(Router);
    if (!auth.isLoggedIn()) {
      return toLogin(router, state);
    }
    return auth.hasFeature(feature) ? true : router.createUrlTree(['/tickets']);
  }
}

export const Auth: ((arg: PermissionCombinator) => CanActivateFn) = (args: PermissionCombinator | undefined = undefined) => {
  return async (_route, state) => {
    const auth = inject(AuthService);
    const router = inject(Router);
    if (!auth.isLoggedIn()) {
      return toLogin(router, state);
    }
    // Ein Direktlink in einem frischen Tab kommt vor der /me-Antwort hier an; bis dahin hieße
    // "nicht geladen" "alles erlaubt". Also erst auf die Rechte warten, dann entscheiden.
    if (args && !auth.isLoaded()) await auth.whenReady();

    if (args && !auth.hasPermission(args)) {
      // Angemeldet, aber ohne Berechtigung: es gibt keine 403-Seite, also zurück
      // auf die Startseite. Ein blankes `false` würde die Navigation nur abbrechen
      // und den User auf einer leeren Seite stehen lassen.
      return router.createUrlTree(['/tickets']);
    }

    return true;
  }
}
